/* eslint-disable react/jsx-props-no-spreading */
import { FC, useEffect } from 'react'
import ReactGA from 'react-ga4'
import { useLocation } from 'react-router-dom'
import type { ITemplate } from '../interfaces/templates'
import Default from './Default'
import { useCheckoutRuleOptions } from '../hooks/checkout'
import { CheckoutRules } from '../interfaces'
import { fireGTMEvent } from '../utils/fireGTMEvent'
import type { CheckoutPickerEntryPoint } from '../interfaces/tag_manager'

/**
 * Payment-method screen, reached at whatever route COM-951 authors for it.
 *
 * Thin by design, like `OrderDetail` beside it: fetch on mount, fire
 * analytics, delegate rendering to `Default`. `PaymentMethodSelector`, its
 * copy, its label map and its actions are authored in SCS config and resolve
 * through `importRoute`, so no layout belongs in this file — and the props
 * config cannot express are injected in `useCinnamonProps`, not passed here.
 * `InjectedPaymentMethodProps` in `utils/paymentMethodSelector.ts` is the
 * list, checked against the library's own prop type.
 *
 * The empty and error states are authored components too, gated on
 * `checkout.ui.PAYMENT_METHODS.{emptyHidden,errorHidden}` through a `hidden`
 * prop. This template renders no copy of its own, and its suite asserts that.
 *
 * ⚠️ **The pilot offers exactly one method, `CREDIT`.** Captured 2026-09-23
 * against a live session, contrary to COM-944's OQ-2, which records the rule
 * as an empty shell. So this screen has something real to render today, and
 * what it renders is a single-option selector.
 *
 * Whether a one-option rule should settle itself and skip the screen is
 * **COM-1048's** question, not this file's. It settles the rule by writing the
 * value and keeps this route authored as the fallback, so the picker stays
 * reachable for a shopper whose auto-write failed. Render the single row.
 *
 * **A selection here reaches `order.paymentMethod`.** The write lands on
 * `session.checkoutRules`, the server copies the offered option onto the
 * pick, and `order.paymentMethod` is the pick's `data` — guide §2 step 4.
 * COM-829 widened `OrderPaymentMethods` to carry `CASH` and `TRANSFER` so
 * the field types line up.
 *
 * ⚠️ **Per-option copy is a money leak if passed raw.** The library's picker
 * takes `tag` and `helperText` per option — *"CASH — $50 de recargo"* — and
 * `maskPricesForDisplay` is an allowlist of prop *names* over *numbers*, so it
 * cannot see a price inside a string. `hidePrices` is injected for exactly
 * that, and the picker suppresses those strings itself.
 *
 * **No route exists in code.** Same as the delivery screen: the route, the
 * container and the navigation onto it are SCS config, authored by COM-951.
 */
const PaymentMethodSelector: FC<ITemplate> = ({ ...props }) => {
  useCheckoutRuleOptions(CheckoutRules.PAYMENT_METHODS)
  const location = useLocation()
  const entryPoint: CheckoutPickerEntryPoint =
    (location.state as { entryPoint?: CheckoutPickerEntryPoint } | null)
      ?.entryPoint ?? 'cart_change'

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: `/${props.container.uid}` })
    window.fbq('track', 'PageView', { page: `/${props.container.uid}` })
    fireGTMEvent('commerce-checkout_picker_open', {
      rule_type: 'payment_method',
      entry_point: entryPoint,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <Default {...props} />
}

export default PaymentMethodSelector
